import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { WrappedStats } from './wrapped-model';

const CATEGORY_COLORS: Record<string, string> = {
  Bier: '#6dbfe8',
  Wein: '#f49bb0',
  Cocktail: '#c7b9e8',
  Sonstiges: '#9edbd4',
};

export function WrappedCharts({
  stats,
  monthlyMetric,
  monthlyTitle,
}: {
  stats: WrappedStats;
  monthlyMetric: 'drinks' | 'drinkingDays';
  monthlyTitle: string;
}) {
  const chartMonths = stats.chartMonths;
  return (
    <section className="wrapped-chart-grid" aria-label="Wrapped-Diagramme">
      <article className="wrapped-panel">
        <div className="section-heading">
          <div>
            <p className="step-label">Monat für Monat</p>
            <h3>{monthlyTitle}</h3>
          </div>
          <span className="people-count">{chartMonths.length} Monate</span>
        </div>
        {chartMonths.length === 0 ? (
          <p className="chart-empty">Noch keine Einträge vorhanden.</p>
        ) : (
          <div className="wrapped-chart" role="img" aria-label={monthlyTitle}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartMonths} margin={{ top: 12, right: 8, left: -16, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d9e0dc" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: '#62737a', fontSize: 12 }} tickMargin={8} />
                <YAxis allowDecimals={false} tick={{ fill: '#62737a', fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => [
                    `${typeof value === 'number' ? value : 0} ${monthlyMetric === 'drinks' ? 'Getränke' : 'Trinktage'}`,
                    monthlyMetric === 'drinks' ? 'Menge' : 'Aktive Tage',
                  ]}
                />
                <Bar
                  dataKey={monthlyMetric}
                  name={monthlyMetric === 'drinks' ? 'Getränke' : 'Trinktage'}
                  fill="#6dbfe8"
                  radius={[7, 7, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </article>

      <article className="wrapped-panel">
        <div className="section-heading">
          <div>
            <p className="step-label">Aufteilung</p>
            <h3>Getränketypen</h3>
          </div>
          <span className="people-count">{stats.totalDrinks} gesamt</span>
        </div>
        {stats.categories.length === 0 ? (
          <p className="chart-empty">Noch keine Einträge vorhanden.</p>
        ) : (
          <div className="wrapped-chart" role="img" aria-label="Kreisdiagramm der Getränketypen">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={stats.categories}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={68}
                  outerRadius={98}
                  paddingAngle={3}
                >
                  {stats.categories.map((category) => (
                    <Cell key={category.name} fill={CATEGORY_COLORS[category.name] ?? '#9edbd4'} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => [
                    `${typeof value === 'number' ? value : 0} Getränke`,
                    'Menge',
                  ]}
                />
                <Legend verticalAlign="bottom" height={32} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </article>
    </section>
  );
}
